import { Component, HostListener, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { FileViewerKind, FileViewerUtils } from "../../utils/file-viewer.util";

export interface CoreFileViewerModalData {
  url: string;
  fileName: string;
  fileType?: string | null;
  secondaryLine?: string;
  onDownload: () => void;
}

@Component({
  selector: "app-core-file-viewer-modal",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fv-modal">
      <header class="fv-modal__header">
        <span class="fv-modal__icon" [ngSwitch]="kind">
          <svg *ngSwitchCase="'image'" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <svg *ngSwitchDefault viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
        </span>
        <span class="fv-modal__title">
          <span class="fv-modal__filename" [title]="data.fileName">{{ data.fileName }}</span>
          @if (data.secondaryLine) {
            <span class="fv-modal__subline">{{ data.secondaryLine }}</span>
          }
        </span>
        <button type="button" class="fv-modal__action" (click)="download()">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
          <span>Download</span>
        </button>
        <button type="button" class="fv-modal__close" aria-label="Close" (click)="close()">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div class="fv-modal__body">
        <iframe *ngIf="kind === 'pdf' && !previewFailed" [src]="safeUrl" class="fv-modal__pdf" title="{{ data.fileName }}"></iframe>
        <div *ngIf="kind === 'image'" class="fv-modal__image-wrap">
          <img [src]="data.url" [alt]="data.fileName" class="fv-modal__image" (error)="onPreviewError()" />
        </div>
        <div *ngIf="previewFailed" class="fv-modal__fallback">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.4">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M9.5 12.5l5 5M14.5 12.5l-5 5" />
          </svg>
          <p class="fv-modal__fallback-text">This file can't be previewed.</p>
          <button type="button" class="fv-modal__fallback-action" (click)="download()">Download instead</button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .fv-modal {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        max-height: 90vh;
      }
      .fv-modal__header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid #eceef1;
        flex-shrink: 0;
      }
      .fv-modal__icon {
        display: flex;
        align-items: center;
        color: #635bff;
        flex-shrink: 0;
      }
      .fv-modal__title {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      .fv-modal__filename {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 600;
        font-size: 14px;
        color: #212636;
      }
      .fv-modal__subline {
        font-size: 12px;
        color: #667085;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .fv-modal__action {
        display: flex;
        align-items: center;
        gap: 6px;
        border: 1px solid #d9d9de;
        background: #fff;
        border-radius: 6px;
        padding: 6px 12px;
        font-size: 13px;
        color: #212636;
        cursor: pointer;
        flex-shrink: 0;
      }
      .fv-modal__action:hover {
        background: #f4f2fe;
        border-color: #635bff;
      }
      .fv-modal__close {
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: #667085;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        flex-shrink: 0;
      }
      .fv-modal__close:hover {
        background: #f1f1f4;
        color: #212636;
      }
      .fv-modal__body {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        background: #f4f2fe;
        display: flex;
      }
      .fv-modal__pdf {
        border: none;
        width: 100%;
        height: 100%;
        min-height: 70vh;
      }
      .fv-modal__image-wrap {
        margin: auto;
        width: 100%;
        display: flex;
        justify-content: center;
      }
      .fv-modal__image {
        width: 100%;
        max-width: 100%;
        height: auto;
        display: block;
      }
      .fv-modal__fallback {
        margin: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        color: #667085;
        text-align: center;
        padding: 24px;
      }
      .fv-modal__fallback-text {
        margin: 0;
        font-size: 14px;
        color: #212636;
      }
      .fv-modal__fallback-action {
        border: 1px solid #d9d9de;
        background: #fff;
        border-radius: 6px;
        padding: 6px 14px;
        font-size: 13px;
        color: #212636;
        cursor: pointer;
      }
      .fv-modal__fallback-action:hover {
        background: #f4f2fe;
        border-color: #635bff;
      }
    `,
  ],
})
export class CoreFileViewerModalComponent implements OnInit {
  private readonly modalRef = inject(NzModalRef);
  private readonly sanitizer = inject(DomSanitizer);
  readonly data = inject<CoreFileViewerModalData>(NZ_MODAL_DATA);

  readonly kind: FileViewerKind = FileViewerUtils.getFileKind(this.data.fileName || this.data.url, this.data.fileType);
  readonly safeUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.url);
  previewFailed = false;

  ngOnInit(): void {
    if (this.kind === "pdf") {
      this.verifyPdfContentType();
    }
  }

  /**
   * Browsers silently render a blank iframe (no error event) when a PDF is served with a wrong
   * Content-Type (e.g. stale `application/octet-stream` from Storage — see HAN-362), so we check
   * the real response header ourselves before committing to the iframe.
   */
  private async verifyPdfContentType(): Promise<void> {
    try {
      const res = await fetch(this.data.url, { method: "HEAD" });
      const contentType = res.headers.get("content-type") || "";
      if (res.ok && contentType && !contentType.includes("pdf")) {
        this.previewFailed = true;
      }
    } catch {
      // Network/CORS failure on the HEAD check isn't reason enough to hide a preview that might work fine.
    }
  }

  onPreviewError(): void {
    this.previewFailed = true;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.close();
  }

  close(): void {
    this.modalRef.close();
  }

  download(): void {
    this.data.onDownload();
  }
}
